import { Component, ElementRef, inject, Input, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { catchError, finalize, of } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';

import { BaseComponent } from 'src/app/@shared/components/abstract/base.component';
import { AgentActionService } from 'src/app/@core/services/ai/agent-action.service';
import { AgentSessionService, AgentTurnUndoResult } from 'src/app/@core/services/ai/agent-session.service';
import { AgentUsage, ConversationMessage } from 'src/app/@core/services/ai/ai-assistant.service';
import { AssistantPanelService } from 'src/app/@core/services/ai/assistant-panel.service';
import { AgentTurnFeedback } from 'src/app/@shared/models/ai/agent-turn.model';
import { AgentAction } from 'src/app/@shared/models/ai/agent-action.model';
import { AgentSession } from 'src/app/@shared/models/ai/agent-session.model';
import { DocLinkTarget } from 'src/app/@shared/components/documentation/documentation-content/documentation-content.component';
import { AssistantActionCardComponent } from '../assistant-action-card/assistant-action-card.component';
import { AssistantComposerComponent } from '../assistant-composer/assistant-composer.component';
import { AssistantConversationComponent, TurnUndoReport } from '../assistant-conversation/assistant-conversation.component';
import { AssistantRecentComponent } from '../assistant-recent/assistant-recent.component';
import { AssistantUsageComponent } from '../assistant-usage/assistant-usage.component';

/** Où l'assistant est affiché : la page pleine largeur, ou le panneau permanent du shell (#1292). */
export type AssistantWorkspaceMode = 'page' | 'panel';

/**
 * Espace de conversation de l'assistant IA, commun à la page pleine largeur et au panneau :
 * charge les données (sessions, conversation), porte le contexte (état du tour, session active)
 * et orchestre les actions. Le rendu est délégué aux composants `assistant-*`.
 *
 * La conversation elle-même vit dans `AiAssistantService` : les deux modes n'en ont qu'une,
 * et passer de l'un à l'autre ne la perd pas.
 */
@Component({
    selector: 'app-assistant-workspace',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ButtonModule,
        DialogModule,
        InputTextModule,
        ProgressSpinnerModule,
        TooltipModule,
        AssistantActionCardComponent,
        AssistantComposerComponent,
        AssistantConversationComponent,
        AssistantRecentComponent,
        AssistantUsageComponent,
    ],
    templateUrl: './assistant-workspace.component.html',
    host: { class: 'block h-full' },
})
export class AssistantWorkspaceComponent extends BaseComponent {

    /*----------------------------------------------------------------------------------------
     * Gestion de la vue
     ----------------------------------------------------------------------------------------*/

    @ViewChild('thread') thread?: ElementRef<HTMLElement>;
    @ViewChild(AssistantComposerComponent) private _composer?: AssistantComposerComponent;

    @Input() mode: AssistantWorkspaceMode = 'page';

    private _actionService = inject(AgentActionService);
    private _panelService = inject(AssistantPanelService);
    private _sessionService = inject(AgentSessionService);

    /*----------------------------------------------------------------------------------------
     * Données
     ----------------------------------------------------------------------------------------*/

    messages: ConversationMessage[] = [];
    streaming: boolean = false;
    statusText: string | null = null;
    activeSessionId: string | null = null;
    sessionUsage: AgentUsage | null = null;
    recentSessions: AgentSession[] = [];
    loadingRecent: boolean = false;
    loadingSession: boolean = false;
    /** Propositions de l'agent en attente (#1217) : les miennes, et celles que je peux appliquer. */
    pendingActions: AgentAction[] = [];
    /** Tour dont l'annulation est en cours. */
    undoingTurnIndex: number | null = null;
    /** Ce que la dernière annulation de tour n'a pas pu défaire, jusqu'à la réouverture de la conversation. */
    turnUndoReport: TurnUndoReport | null = null;
    /** Conversation d'un autre utilisateur, lue par la supervision : ses avis ne se modifient pas. */
    feedbackReadonly: boolean = false;

    renameDialogVisible: boolean = false;
    renameTitle: string = '';
    private _renameTarget: AgentSession | null = null;

    /*----------------------------------------------------------------------------------------
     * Accesseurs
     ----------------------------------------------------------------------------------------*/

    /** Vue conversation dès qu'un message existe ou qu'un tour est en cours ; sinon accueil. */
    public get inConversation(): boolean {
        return this.messages.length > 0 || this.streaming;
    }

    public get isPanel(): boolean {
        return this.mode === 'panel';
    }

    private _sessionId: string | null = null;
    public get sessionId(): string | null {
        return this._sessionId;
    }
    /**
     * Conversation désignée par l'URL de la page (`/assistant/session/<uuid>`) : elle est
     * chargée si ce n'est pas déjà celle de la session active. Le panneau n'en reçoit pas.
     */
    @Input() public set sessionId(value: string | null) {
        this._sessionId = value;
        if (value && value !== this.activeSessionId) {
            this._loadSession(value);
        }
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes publiques
     ----------------------------------------------------------------------------------------*/

    constructor() {
        super();

        this.subscriptions.add(
            this.aiAssistantService.conversationMessages$.subscribe(messages => {
                this.messages = messages;
                setTimeout(() => this._scrollToBottom());
                // Retour à l'accueil (nouvelle conversation) : les propositions ont pu changer.
                if (!messages.length) {
                    this.turnUndoReport = null;
                    this._loadPendingActions();
                }
            })
        );
        this.subscriptions.add(
            this.aiAssistantService.statusText$.subscribe(status => {
                this.statusText = status;
                setTimeout(() => this._scrollToBottom());
            })
        );
        this.subscriptions.add(
            this.aiAssistantService.sessionId$.subscribe(id => this.activeSessionId = id)
        );
        this.subscriptions.add(
            this.aiAssistantService.sessionUsage$.subscribe(usage => this.sessionUsage = usage)
        );
        this.subscriptions.add(
            this.aiAssistantService.streaming$.subscribe(streaming => {
                const wasStreaming = this.streaming;
                this.streaming = streaming;
                // Fin d'un tour : la liste a pu changer (nouvelle session, titre, ordre)
                // et on redonne le focus au champ pour enchaîner.
                if (wasStreaming && !streaming) {
                    this._loadRecentSessions();
                    setTimeout(() => this._composer?.focus());
                }
            })
        );

    }

    public override ngAfterViewInit(): void {
        super.ngAfterViewInit();
        // Le panneau s'ouvre pour qu'on y écrive : le champ a le focus d'emblée
        if (this.isPanel) {
            setTimeout(() => this._composer?.focus());
        }
    }

    public override ngOnInit(): void {
        super.ngOnInit();
        this._loadRecentSessions();
    }

    /** Une proposition de l'accueil a été appliquée ou refusée : la liste se relit. */
    public onChangeAction(): void {
        this._loadPendingActions();
    }

    public onChangeFeedback(change: { turnIndex: number; feedback: AgentTurnFeedback | null; feedbackComment: string }): void {
        this.aiAssistantService.setTurnFeedback(change.turnIndex, change.feedback, change.feedbackComment);
    }

    public onClickClose(): void {
        this._panelService.close();
    }

    public onClickExpand(): void {
        this._panelService.expand(this.activeSessionId);
    }

    public onClickNewConversation(): void {
        this._startNewConversation();
    }

    public onClickReduce(): void {
        this._panelService.reduce();
    }

    public onConfirmRename(): void {
        this._rename();
    }

    public onDeleteSession(session: AgentSession): void {
        this._confirmDelete(session);
    }

    /** Citation du manuel : l'article s'ouvre dans l'application. */
    public onOpenDoc(target: DocLinkTarget): void {
        this._goToArticle(target);
    }

    public onOpenSession(session: AgentSession): void {
        this._navigateToSession(session.sid);
    }

    public onRenameSession(session: AgentSession): void {
        this._openRenameDialog(session);
    }

    public onSendText(text: string): void {
        this.aiAssistantService.sendText(text);
    }

    public onUndoTurn(turnIndex: number): void {
        this._confirmUndoTurn(turnIndex);
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes privées
     ----------------------------------------------------------------------------------------*/

    /**
     * Une ligne par action que le tour n'a pas pu défaire : son récapitulatif, tel que la
     * carte l'affiche, et le motif du serveur. `null` quand tout a été défait.
     */
    private _buildTurnUndoReport(turnIndex: number, results: AgentTurnUndoResult[]): TurnUndoReport | null {
        const actions = this.messages.flatMap(message => message.actions ?? []);
        const lines = results
            .filter(result => result.status >= 400)
            .map(result => {
                const summary = actions.find(action => action.id === result.actionId)?.summary;
                const reason = result.messages.map(message => message.longText || message.shortText).filter(Boolean).join(' ');
                return summary ? `${summary} : ${reason}` : reason;
            });
        return lines.length ? { turnIndex, lines } : null;
    }

    private _confirmDelete(session: AgentSession): void {
        this.confirmAction('DELETE_AGENT_SESSION', () => this._deleteSession(session));
    }

    private _confirmUndoTurn(turnIndex: number): void {
        this.confirmAction('UNDO_AGENT_TURN', () => this._undoTurn(turnIndex));
    }

    private _deleteSession(session: AgentSession): void {
        this._sessionService.remove$(session).subscribe(() => {
            if (this.activeSessionId === session.sid) this.aiAssistantService.newConversation();
            this._loadRecentSessions();
            // Supprimer une conversation refuse ses propositions en attente.
            this._loadPendingActions();
        });
    }

    /**
     * Ouvre l'article cité. Un identifiant absent du manuel du pays est un lien
     * mort : mieux vaut ne rien faire que de mener à une page « article
     * introuvable » — le modèle a pu recopier une citation de travers.
     */
    private _goToArticle(target: DocLinkTarget): void {
        this.subscriptions.add(
            this.documentationService.loadIndex$().subscribe({
                next: () => {
                    if (!this.documentationService.article(target.id)) {
                        return;
                    }
                    this.router.navigate([this.appService.clientUrl('/documentation'), target.id], target.anchor ? { fragment: target.anchor } : {});
                },
                error: () => undefined,
            })
        );
    }

    private _loadPendingActions(): void {
        this._actionService.listProposed$().subscribe(actions => {
            this.pendingActions = actions ?? [];
        });
    }

    /** Les conversations récentes sont l'historique de la page pleine largeur : le panneau n'en liste pas. */
    private _loadRecentSessions(): void {
        if (this.isPanel) return;
        this.loadingRecent = true;
        this._sessionService.list$().pipe(
            finalize(() => this.loadingRecent = false)
        ).subscribe(sessions => {
            this.recentSessions = (sessions ?? []).slice(0, 10);
        });
    }

    private _loadSession(id: string): void {
        this.turnUndoReport = null;
        this.loadingSession = true;
        this._sessionService.open$(id).pipe(
            finalize(() => this.loadingSession = false)
        ).subscribe(detail => {
            if (detail) this._showSession(detail);
        });
    }

    private _navigateToSession(id: string): void {
        this.router.navigate([this.appService.clientUrl('/assistant/session'), id]);
    }

    private _openRenameDialog(session: AgentSession): void {
        this._renameTarget = session;
        this.renameTitle = session.title;
        this.renameDialogVisible = true;
    }

    /** Relit la conversation affichée, sans l'écran de chargement : les cartes et les boutons se mettent à jour. */
    private _refreshSession(id: string): void {
        this._sessionService.open$(id).subscribe(detail => {
            if (detail) this._showSession(detail);
        });
    }

    private _rename(): void {
        const title = this.renameTitle.trim();
        if (!this._renameTarget || !title) return;
        this._sessionService.rename$(this._renameTarget, title).subscribe(() => {
            this.renameDialogVisible = false;
            this._renameTarget = null;
            this._loadRecentSessions();
        });
    }

    private _scrollToBottom(): void {
        const el = this.thread?.nativeElement;
        if (el) el.scrollTop = el.scrollHeight;
    }

    /**
     * Affiche la conversation lue. Seul son propriétaire donne un avis : le serveur ne sert
     * `user` qu'à la supervision, qui lit aussi les conversations des autres.
     */
    private _showSession(detail: AgentSession): void {
        this.feedbackReadonly = !!detail.user && detail.user.id !== this.appService.user?.id;
        this.aiAssistantService.loadSession(detail.sid, detail.turns, detail.usage);
    }

    /**
     * Retour à l'accueil. Sur la page, l'URL de la conversation quittée est elle aussi
     * abandonnée : rechargée, elle rouvrirait la conversation qu'on vient de fermer.
     */
    private _startNewConversation(): void {
        this.aiAssistantService.newConversation();
        if (!this.isPanel && this._sessionId) {
            this.router.navigate([this.appService.clientUrl('/assistant')]);
        }
        setTimeout(() => this._composer?.focus());
    }

    /**
     * « Annuler cet échange » : le bilan s'affiche en message, le détail de ce qui n'a pas pu
     * être défait sous les cartes du tour, puis la conversation est relue.
     */
    private _undoTurn(turnIndex: number): void {
        const sessionId = this.activeSessionId;
        if (!sessionId) return;
        this.undoingTurnIndex = turnIndex;
        this.subscriptions.add(
            this._sessionService.undoTurn$(sessionId, turnIndex).pipe(
                catchError(() => of(null)),
                finalize(() => this.undoingTurnIndex = null)
            ).subscribe(results => {
                if (results) this.turnUndoReport = this._buildTurnUndoReport(turnIndex, results);
                this._refreshSession(sessionId);
            })
        );
    }
}
